import {
  RestrictivePracticeInterventionTypeEnum,
  RestrictivePracticeMethodEnum,
  RestrictivePracticeOutcomeEnum,
  RestrictivePracticeTypeEnum
} from '@lmc/validations'
import { incidentReportsConstants } from 'locale/incidentReports'

/** Each code's locale key, in the order the design lists them. */
export const PRACTICE_TYPE_KEYS: Record<RestrictivePracticeTypeEnum, string> = {
  [RestrictivePracticeTypeEnum.physicalIntervention]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE_PHYSICAL_INTERVENTION,
  [RestrictivePracticeTypeEnum.mechanicalRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE_MECHANICAL_RESTRAINT,
  [RestrictivePracticeTypeEnum.chemicalRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE_CHEMICAL_RESTRAINT,
  [RestrictivePracticeTypeEnum.environmentalRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE_ENVIRONMENTAL_RESTRAINT,
  [RestrictivePracticeTypeEnum.verbalRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE_VERBAL_RESTRAINT
}

/** Only a physical intervention has a method. */
export const METHOD_KEYS: Record<RestrictivePracticeMethodEnum, string> = {
  [RestrictivePracticeMethodEnum.guidingOrEscorting]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_GUIDING_OR_ESCORTING,
  [RestrictivePracticeMethodEnum.holding]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_HOLDING,
  [RestrictivePracticeMethodEnum.seatedRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_SEATED_RESTRAINT,
  [RestrictivePracticeMethodEnum.standingRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_STANDING_RESTRAINT,
  [RestrictivePracticeMethodEnum.floorRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_FLOOR_RESTRAINT,
  [RestrictivePracticeMethodEnum.protectiveOrDefensiveIntervention]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_PROTECTIVE_OR_DEFENSIVE,
  [RestrictivePracticeMethodEnum.mechanicalRestraint]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_MECHANICAL_RESTRAINT,
  [RestrictivePracticeMethodEnum.otherRestrictivePhysicalIntervention]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD_OTHER
}

export const INTERVENTION_TYPE_KEYS: Record<
  RestrictivePracticeInterventionTypeEnum,
  string
> = {
  [RestrictivePracticeInterventionTypeEnum.planned]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_INTERVENTION_PLANNED,
  [RestrictivePracticeInterventionTypeEnum.unplanned]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_INTERVENTION_UNPLANNED
}

export const OUTCOME_KEYS: Record<RestrictivePracticeOutcomeEnum, string> = {
  [RestrictivePracticeOutcomeEnum.riskReducedOrPrevented]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_RISK_REDUCED,
  [RestrictivePracticeOutcomeEnum.calmedAndEndedSafely]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_CALMED_AND_ENDED_SAFELY,
  [RestrictivePracticeOutcomeEnum.noInjuriesSustained]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_NO_INJURIES,
  [RestrictivePracticeOutcomeEnum.injuriesOrDistressExperienced]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_INJURIES_OR_DISTRESS,
  [RestrictivePracticeOutcomeEnum.damageToPropertyOrEnvironment]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_DAMAGE_TO_PROPERTY,
  [RestrictivePracticeOutcomeEnum.medicalAssessmentOrTreatmentRequired]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_MEDICAL_ASSESSMENT,
  [RestrictivePracticeOutcomeEnum.returnedToUsualActivities]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_RETURNED_TO_USUAL_ACTIVITIES,
  [RestrictivePracticeOutcomeEnum.followUpActionsRequired]:
    incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_FOLLOW_UP_ACTIONS
}

/** The locale key for `code` in one of the maps above, or the code itself. */
export const keyFor = <Code extends string>(
  keys: Record<Code, string>,
  code: Code
): string => {
  const key = keys[code]
  if (!key) {
    // A code the app doesn't know yet: show it rather than nothing, and say so.
    console.warn(`No locale key for restrictive practice code "${code}"`)
  }
  return key ?? code
}
